/**
 * Remote-control transport: a low-latency WebRTC DataChannel between the host
 * tab (the emulator) and the phone controller page, established over a pluggable
 * signaling channel. The 20-byte INPUT frames (protocol.ts) travel on an
 * unreliable/unordered channel — for a live controller, the freshest frame is
 * all that matters, so dropping a late packet beats head-of-line blocking (the
 * receiver already reorders by seq and decays to neutral, see inject.ts).
 *
 * Signaling (the one-time SDP/ICE exchange to open the channel) is abstracted
 * behind SignalingChannel so the WebRTC logic is testable/movable and the actual
 * rendezvous — a tiny relay keyed by the pairing session id — is swappable. The
 * live end-to-end (a phone on the network + a relay) is the manual check
 * documented in docs/HANDOFF.md, exactly like the WebGPU/audio checks.
 *
 * §1.6: input plumbing only.
 */

/** A message on the signaling channel: an SDP offer/answer or a trickled ICE
 *  candidate, tagged so the peer can route it. Plain JSON, relay-agnostic. */
export type SignalMessage =
  | { kind: "offer"; sdp: string }
  | { kind: "answer"; sdp: string }
  | { kind: "ice"; candidate: RTCIceCandidateInit };

/** The rendezvous both peers speak to swap SignalMessages for one session. A
 *  concrete impl (WebSocket relay, BroadcastChannel for same-device dev, etc.)
 *  is injected; this module never assumes one. */
export interface SignalingChannel {
  send(msg: SignalMessage): void;
  onMessage(handler: (msg: SignalMessage) => void): void;
  close(): void;
}

/** Public STUN only — no TURN, no relay of game data; the DataChannel is P2P.
 *  Overridable for self-hosting. */
export const DEFAULT_ICE: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

export type LinkState = "connecting" | "connected" | "disconnected" | "failed";

/** A live remote link. `send` pushes a raw INPUT frame (host->phone is unused
 *  today but symmetric); `onData` delivers received frames; `onState` reports
 *  connection transitions so the UI can show pairing status. */
export interface RemoteLink {
  send(bytes: Uint8Array<ArrayBuffer>): void;
  onData(handler: (data: ArrayBuffer) => void): void;
  onState(handler: (state: LinkState) => void): void;
  close(): void;
}

const DATA_LABEL = "voland-input";

/** Shared plumbing: wire a peer connection's lifecycle + a data channel into the
 *  RemoteLink surface, forwarding ICE out over signaling. */
function makeLink(pc: RTCPeerConnection, signaling: SignalingChannel): {
  link: RemoteLink;
  attachChannel: (ch: RTCDataChannel) => void;
} {
  let channel: RTCDataChannel | null = null;
  const dataHandlers: ((d: ArrayBuffer) => void)[] = [];
  const stateHandlers: ((s: LinkState) => void)[] = [];
  const emitState = (s: LinkState): void => { for (const h of stateHandlers) h(s); };

  pc.onicecandidate = (e): void => {
    if (e.candidate) signaling.send({ kind: "ice", candidate: e.candidate.toJSON() });
  };
  pc.onconnectionstatechange = (): void => {
    const s = pc.connectionState;
    if (s === "connected") emitState("connected");
    else if (s === "disconnected" || s === "closed") emitState("disconnected");
    else if (s === "failed") emitState("failed");
  };

  const attachChannel = (ch: RTCDataChannel): void => {
    channel = ch;
    ch.binaryType = "arraybuffer";
    ch.onmessage = (e): void => {
      const d = e.data;
      const ab = d instanceof ArrayBuffer ? d : null;
      if (ab) for (const h of dataHandlers) h(ab);
    };
    ch.onopen = (): void => emitState("connected");
    ch.onclose = (): void => emitState("disconnected");
  };

  const link: RemoteLink = {
    send(bytes: Uint8Array<ArrayBuffer>): void {
      if (channel && channel.readyState === "open") channel.send(bytes);
    },
    onData(h): void { dataHandlers.push(h); },
    onState(h): void { stateHandlers.push(h); },
    close(): void {
      try { channel?.close(); } catch { /* already gone */ }
      try { pc.close(); } catch { /* already gone */ }
      try { signaling.close(); } catch { /* already gone */ }
    },
  };
  return { link, attachChannel };
}

/**
 * HOST side (the emulator tab): creates the peer connection and the DataChannel,
 * sends the offer, and applies the phone's answer + ICE. Call after the phone
 * has joined the same signaling session (it scanned the pairing QR).
 */
export async function createHostLink(
  signaling: SignalingChannel,
  iceServers: RTCIceServer[] = DEFAULT_ICE,
): Promise<RemoteLink> {
  const pc = new RTCPeerConnection({ iceServers });
  const { link, attachChannel } = makeLink(pc, signaling);

  /* Unreliable + unordered: newest-frame-wins beats retransmit for a live pad. */
  const ch = pc.createDataChannel(DATA_LABEL, { ordered: false, maxRetransmits: 0 });
  attachChannel(ch);

  signaling.onMessage((msg): void => {
    if (msg.kind === "answer") void pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
    else if (msg.kind === "ice") void pc.addIceCandidate(msg.candidate).catch(() => {});
  });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  signaling.send({ kind: "offer", sdp: offer.sdp ?? "" });
  return link;
}

/**
 * CONTROLLER side (the phone page): waits for the host's offer, answers it, and
 * adopts the host-created DataChannel. Mirrors createHostLink.
 */
export async function createControllerLink(
  signaling: SignalingChannel,
  iceServers: RTCIceServer[] = DEFAULT_ICE,
): Promise<RemoteLink> {
  const pc = new RTCPeerConnection({ iceServers });
  const { link, attachChannel } = makeLink(pc, signaling);

  pc.ondatachannel = (e): void => attachChannel(e.channel);

  signaling.onMessage((msg): void => {
    if (msg.kind === "offer") {
      void (async (): Promise<void> => {
        await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        signaling.send({ kind: "answer", sdp: answer.sdp ?? "" });
      })();
    } else if (msg.kind === "ice") {
      void pc.addIceCandidate(msg.candidate).catch(() => {});
    }
  });
  return link;
}

/**
 * A WebSocket signaling relay client: both peers connect to the same relay URL
 * with the pairing session id, and the relay forwards SignalMessages between the
 * two members of that session. The relay is a ~30-line broadcast server (see
 * docs/HANDOFF.md); it never sees game data or keys, only SDP/ICE for one
 * session. Same-origin (wss://<app-host>/signal) keeps cross-origin isolation
 * intact for the SharedArrayBuffer.
 */
export class WebSocketSignaling implements SignalingChannel {
  private ws: WebSocket;
  private readonly queue: SignalMessage[] = [];
  private handler: ((msg: SignalMessage) => void) | null = null;

  constructor(url: string, sessionId: string, role: "host" | "controller") {
    const sep = url.includes("?") ? "&" : "?";
    this.ws = new WebSocket(`${url}${sep}s=${encodeURIComponent(sessionId)}&role=${role}`);
    this.ws.onopen = (): void => { for (const m of this.queue.splice(0)) this.rawSend(m); };
    this.ws.onmessage = (e): void => {
      let msg: SignalMessage | null = null;
      try { msg = JSON.parse(typeof e.data === "string" ? e.data : "") as SignalMessage; }
      catch { msg = null; }
      if (msg && this.handler) this.handler(msg);
    };
  }
  private rawSend(msg: SignalMessage): void { this.ws.send(JSON.stringify(msg)); }
  send(msg: SignalMessage): void {
    if (this.ws.readyState === WebSocket.OPEN) this.rawSend(msg);
    else this.queue.push(msg);
  }
  onMessage(handler: (msg: SignalMessage) => void): void { this.handler = handler; }
  close(): void { try { this.ws.close(); } catch { /* already closed */ } }
}

/** Generate a short, URL-safe pairing session id (no crypto dependency; enough
 *  entropy to keep two concurrent pairings on a shared relay from colliding). */
export function newSessionId(): string {
  const bytes = new Uint8Array(9);
  (globalThis.crypto ?? ({} as Crypto)).getRandomValues?.(bytes);
  let s = "";
  const alpha = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  for (const b of bytes) s += alpha[b % alpha.length];
  return `${s.slice(0, 4)}-${s.slice(4, 8)}`;
}
